import { useCallback, useRef, useEffect } from 'react'
import { LocationSuggestionOptionValue } from '@dcxwam/dcx-wam-ui-foundations'
import { GeoLocation } from '@dcxwam/stores-sdk'
import { pushDataLayer } from '../../../../../utils/datalayer'
import { buildDataLayerStoreSearch } from '../../common/utils'

interface UseLocationSearchParams {
  setSearchParams: (
    params: Record<string, string>,
    options?: { replace?: boolean }
  ) => void
  currentLocationLabel?: string
  initialLatitude: number
  initialLongitude: number
  initialQuery: string | null
  onLocationChange?: (
    latitude: number,
    longitude: number,
    query: string
  ) => void
  searchParams: URLSearchParams
}

interface UseLocationSearchReturn {
  handleLocationSearchChange: (
    newValue: string | object | null
  ) => Promise<void>
  handleCurrentLocation: (location: {
    latitude: number
    longitude: number
  }) => void
  updateLocationAndReset: (
    latitude: number,
    longitude: number,
    query: string
  ) => void
  handleDragEnd: (geoLocation: GeoLocation) => void
}

export const useLocationSearch = (
  params: UseLocationSearchParams
): UseLocationSearchReturn => {
  const {
    setSearchParams,
    currentLocationLabel,
    initialLatitude,
    initialLongitude,
    initialQuery,
    onLocationChange,
    searchParams
  } = params

  const initialLocationRef = useRef({
    latitude: initialLatitude,
    longitude: initialLongitude,
    query: initialQuery ?? ''
  })

  const searchParamsRef = useRef(searchParams)

  useEffect(() => {
    searchParamsRef.current = searchParams
  }, [searchParams])

  useEffect(() => {
    if (
      initialLatitude &&
      initialLongitude &&
      !initialLocationRef.current.latitude
    ) {
      initialLocationRef.current = {
        latitude: initialLatitude,
        longitude: initialLongitude,
        query: initialQuery ?? ''
      }
    }
  }, [initialLatitude, initialLongitude, initialQuery])

  const updateLocationAndReset = useCallback(
    (latitude: number, longitude: number, query: string) => {
      setSearchParams(
        {
          lat: String(latitude),
          lng: String(longitude),
          query
        },
        { replace: true }
      )

      onLocationChange?.(latitude, longitude, query)
    },
    [setSearchParams, onLocationChange]
  )

  const handleLocationSearchChange = useCallback(
    async (newValue: string | object | null) => {
      if (!newValue) {
        const { latitude, longitude, query } = initialLocationRef.current
        if (latitude && longitude) {
          updateLocationAndReset(latitude, longitude, query)
        }
        return
      }

      const loc = (newValue as LocationSuggestionOptionValue).value

      if (loc?.fetchFields) {
        await loc?.fetchFields({
          fields: ['displayName', 'location']
        })

        if (!loc?.location) {
          return
        }

        const newLat = loc.location.lat()
        const newLng = loc.location.lng()
        const newQuery = loc.displayName
        pushDataLayer(buildDataLayerStoreSearch(newQuery))
        updateLocationAndReset(newLat, newLng, newQuery)
      }
    },
    [updateLocationAndReset]
  )

  const handleCurrentLocation = useCallback(
    (location: { latitude: number; longitude: number }) => {
      updateLocationAndReset(
        location.latitude,
        location.longitude,
        currentLocationLabel ?? ''
      )
    },
    [updateLocationAndReset, currentLocationLabel]
  )

  const handleDragEnd = useCallback(
    (geoLocation: GeoLocation) => {
      setSearchParams(
        {
          ...(searchParamsRef.current
            ? Object.fromEntries(searchParamsRef.current)
            : {}),
          lat: geoLocation.latitude.toString(),
          lng: geoLocation.longitude.toString()
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  return {
    handleLocationSearchChange,
    handleCurrentLocation,
    updateLocationAndReset,
    handleDragEnd
  }
}
